import React, { createContext, useContext, useState, useEffect } from "react";
import { storage, STORAGE_KEYS } from "../utils/storage";
import { tokenStorage } from "../utils/tokenStorage";
import { getStoredUtmParams } from "../hooks/useUtmParams";
import WebSocketService from "../services/WebSocket";
import ApiService from "../services/ApiService.js";
import analyticsService from "../services/AnalyticsService";
import { auth } from "../config/firebase";
import { signOut as firebaseSignOut } from "firebase/auth";


const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const logout = React.useCallback(async () => {
    try {
      // Log logout to analytics
      analyticsService.logLogout();
      
      // Close WebSocket first
      WebSocketService.close();

      // Also sign out from Firebase to clear Google session (ensures chooser next time)
      try { firebaseSignOut(auth); } catch (e) { /* ignore */ }

      storage.removeItem(STORAGE_KEYS.USER);
      await tokenStorage.clearTokens();
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      return { success: true };
    } catch (error) {
      console.error("Error logging out:", error);
      return { success: false, error: "Logout failed" };
    }
  }, []);

  const logoutWithSessionExpired = React.useCallback(async () => {
    setSessionExpired(true);
    await logout();
  }, [logout]);

  const clearSessionExpired = React.useCallback(() => {
    setSessionExpired(false);
  }, []);

  useEffect(() => {
    ApiService.setLogoutFunction(logout);
    ApiService.setSessionExpiredFunction(logoutWithSessionExpired);
  }, [logout, logoutWithSessionExpired]);

  const checkAuthStatus = async () => {
    try {
      const userData = storage.getItem(STORAGE_KEYS.USER);
      const storedToken = await tokenStorage.getAccessToken();

      if (userData && storedToken) {
        setUser(userData);
        setToken(storedToken);
        setIsAuthenticated(true);
        
        // Встановлюємо користувача в аналітику
        if (userData.id) {
          analyticsService.setUserId(userData.id.toString());
          analyticsService.setUserProperties({
            email: userData.email,
            name: userData.name,
            language: userData.language || 'uk'
          });
        }
      }
    } catch (error) {
      console.error("Error checking auth status:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    try {
      const response = await ApiService.post("auth/login", { email, password });

      if (response.data.access_token) {
        const userData = {
          id: response.data.user?.id || Date.now(),
          email: response.data.user?.email || email,
          name: response.data.user?.first_name || email.split("@")[0],
          avatar: response.data.user?.avatar || null,
          createdAt: response.data.user?.createdAt || new Date().toISOString(),
        };

        // Store token and user data
        await tokenStorage.setTokens(
          response.data.access_token,
          response.data.refresh_token,
        );
        const userSuccess = storage.setItem(STORAGE_KEYS.USER, userData);

        if (userSuccess) {
          setUser(userData);
          setToken(response.data.access_token);
          setIsAuthenticated(true);

          // Sync preferred language saved locally to the server (best-effort)
          try {
            const savedLanguage = storage.getItem(STORAGE_KEYS.LANGUAGE);
            if (savedLanguage) {
              await ApiService.patch("users", { language: savedLanguage });
            }
          } catch (e) {
            return {
              success: false,
              error: "Failed to update language",
            };
          }

          // REMOVED: WebSocket connection will be handled by useEffect below
          // WebSocketService.connect(response.data.access_token);

          return { success: true };
        } else {
          return {
            success: false,
            error: "Failed to save authentication data",
          };
        }
      }
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.detail || error?.message,
          status: error.response.status,
        };
      } else {
        return { success: false, error: error?.message };
      }
    }
  };

  // Google login using Firebase ID token -> backend exchange
  const loginWithGoogle = async (googlePayload) => {
    try {
      // Support both legacy string token and new structured payload
      const payload =
        typeof googlePayload === "string"
          ? { id_token: googlePayload }
          : googlePayload;
      // Analytics: start backend exchange (web)
      try { analyticsService.logEvent('google_exchange_start_web', { ts: new Date().toISOString() }); } catch (_) {}
      const response = await ApiService.post("auth/google", payload);

      if (response.data?.access_token) {
        const userData = {
          id: response.data.user?.id || Date.now(),
          email: response.data.user?.email,
          name: response.data.user?.first_name || response.data.user?.name || "",
          avatar: response.data.user?.avatar || null,
          createdAt: response.data.user?.createdAt || new Date().toISOString(),
        };

        await tokenStorage.setTokens(
          response.data.access_token,
          response.data.refresh_token,
        );
        const userSuccess = storage.setItem(STORAGE_KEYS.USER, userData);

        if (userSuccess) {
          setUser(userData);
          setToken(response.data.access_token);
          setIsAuthenticated(true);

        // Log successful Google login
        try {
          analyticsService.logLogin('google');
          analyticsService.logEvent('google_exchange_success_web', { ts: new Date().toISOString() });
        } catch (e) {}

          return { success: true };
        } else {
          return {
            success: false,
            error: "Failed to save authentication data",
          };
        }
      } else {
        return { success: false, error: "Invalid response from server" };
      }
    } catch (error) {
      // Analytics: error on backend exchange (web)
      try {
        analyticsService.logEvent('google_exchange_error_web', {
          status: error?.response?.status,
          detail: error?.response?.data?.detail || error?.message,
          ts: new Date().toISOString(),
        });
      } catch (_) {}
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.detail || error?.message,
          status: error.response.status,
        };
      } else {
        return { success: false, error: error?.message };
      }
    }
  };

  const register = async (name, email, password, confirmPassword) => {
    try {
      // Align payload with backend contract (see mobile and API examples)
      const language = storage.getItem(STORAGE_KEYS.LANGUAGE) || "uk";
      const utmParams = getStoredUtmParams() || {};
      const response = await ApiService.post("auth/register", {
        email: email,
        password: password,
        password_confirmation: confirmPassword,
        first_name: name,
        last_name: "",
        accepted_agreement: true,
        language,
        ...utmParams,
      });

      // If backend returns tokens on registration, authenticate immediately
      if (response.status === 200 || response.status === 201) {
        const data = response.data || {};
        const accessToken = data.access_token || data.token || data.accessToken;
        const refreshToken = data.refresh_token || data.refreshToken;

        if (accessToken) {
          const userData = {
            id: data.user?.id || Date.now(),
            email: data.user?.email || email,
            name: data.user?.first_name || name || email.split("@")[0],
            lastName: data.user?.last_name || "",
            avatar: data.user?.avatar || null,
            createdAt: data.user?.createdAt || new Date().toISOString(),
            acceptedAgreement: data.user?.accepted_agreement ?? true,
            isVerified: data.user?.is_verified ?? true,
            language: data.user?.language || language,
          };

          const tokenSuccess = storage.setItem(STORAGE_KEYS.TOKEN, accessToken);
          const refreshTokenSuccess = refreshToken
            ? storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
            : true;
          const userSuccess = storage.setItem(STORAGE_KEYS.USER, userData);

          if (tokenSuccess && userSuccess && refreshTokenSuccess) {
            setUser(userData);
            setToken(accessToken);
            setIsAuthenticated(true);

            // Best-effort: sync language (ignore failures)
            try {
              await ApiService.patch("users", { language });
            } catch (e) {}

            return { success: true, authenticated: true };
          } else {
            return { success: false, error: "Failed to save authentication data" };
          }
        }

        // No token returned – treat as verification flow
        return { success: true, authenticated: false, requiresVerification: true };
      }
      return { success: false, error: "Invalid response from server" };
    } catch (error) {
      if (error.response) {
        // Server responded with error status
        return {
          success: false,
          error: error.response.data?.detail || error.message,
          status: error.response.status,
        };
      } else {
        // Other error
        return { success: false, error: error.message };
      }
    }
  };

  const getUserInfo = async () => {
    try {
      const { data } = await ApiService.get("users/me");

      if (data) {
        // merge with existing user data
        const updatedUserData = { ...user, ...data };

        storage.setItem(STORAGE_KEYS.USER, updatedUserData);
        setUser(updatedUserData);

        return { success: true, data: updatedUserData };
      }
      return { success: false, error: "User not found" };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error?.message,
      };
    }
  };

  const updateUser = async (userData) => {
    try {
      const response = await ApiService.patch("users", userData);

      if (response.status === 200 || response.status === 204) {
        const updatedUser = { ...user, ...userData };
        const success = storage.setItem(STORAGE_KEYS.USER, updatedUser);

        if (success) {
          setUser(updatedUser);
          return { success: true, data: response.data };
        } else {
          return { success: false, error: "Failed to update user data" };
        }
      } else {
        return { success: false, error: "Failed to update user on server" };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error?.message,
      };
    }
  };

  // Change password method
  const changePassword = async ({
    current_password,
    new_password,
    confirm_password,
  }) => {
    try {
      const response = await ApiService.post("auth/change-password", {
        current_password,
        new_password,
        confirm_password,
      });

      if (response.status === 200 || response.status === 204) {
        return { success: true };
      } else {
        return { success: false, error: "Failed to change password" };
      }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.detail[0].msg ||
          error.response?.data?.detail ||
          error?.message,
      };
    }
  };

  // IMPROVED: Single useEffect to manage WebSocket connection
  useEffect(() => {
    if (isAuthenticated && token) {
      try {
        WebSocketService.connect(token);
      } catch (error) {
        console.error("AuthContext: Error calling WebSocketService.connect:", error);
      }
    } else {
      WebSocketService.close();
    }

    // Cleanup on unmount
    return () => {
      WebSocketService.close();
    };
  }, [isAuthenticated, token]);

  // Verify email with verification code
  const verifyEmail = async (email, verificationCode) => {
    try {
      const response = await ApiService.post("auth/verify-email", {
        email: email,
        code: verificationCode,
      });

      if (response.data.access_token) {
        const userData = {
          id: response.data.user?.id || Date.now(),
          email: response.data.user?.email || email,
          name: response.data.user?.first_name || email.split("@")[0],
          lastName: response.data.user?.last_name || "",
          avatar: response.data.user?.avatar || null,
          createdAt: response.data.user?.createdAt || new Date().toISOString(),
          acceptedAgreement: response.data.user?.accepted_agreement || false,
          isVerified: response.data.user?.is_verified || true,
          language: response.data.user?.language || "uk",
        };

        // Store token and user data
        const tokenSuccess = storage.setItem(
          STORAGE_KEYS.TOKEN,
          response.data.access_token,
        );
        const refreshTokenSuccess = storage.setItem(
          STORAGE_KEYS.REFRESH_TOKEN,
          response.data.refresh_token,
        );
        const userSuccess = storage.setItem(STORAGE_KEYS.USER, userData);

        if (tokenSuccess && userSuccess && refreshTokenSuccess) {
          setUser(userData);
          setToken(response.data.access_token);
          setIsAuthenticated(true);

          // Sync preferred language saved locally to the server (best-effort)
          try {
            const savedLanguage = storage.getItem(STORAGE_KEYS.LANGUAGE);
            if (savedLanguage) {
              await ApiService.patch("users", { language: savedLanguage });
            } else {
              await ApiService.patch("users", { language: "uk" });
            }
          } catch (e) {
            console.warn("Failed to sync language after verification:", e);
          }

          return { success: true };
        } else {
          return {
            success: false,
            error: "Failed to save authentication data",
          };
        }
      } else {
        return { success: false, error: "Invalid response from server" };
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 404) {
          return {
            success: false,
            error: "Виникла помилка. Спробуйте пізніше.",
          };
        } else if (error.response.status === 400) {
          return {
            success: false,
            error: error.response.data?.detail || "Неправильний код підтвердження. Спробуйте ще раз.",
          };
        }
        return {
          success: false,
          error: error.response.data?.detail || error.message,
        };
      } else {
        return { success: false, error: error.message };
      }
    }
  };

  // Resend verification code
  const resendVerificationCode = async (email) => {
    try {
      const response = await ApiService.post("auth/resend-verification", {
        email: email,
      });

      if (response.status === 200 || response.status === 201) {
        return { success: true };
      } else {
        return { success: false, error: "Failed to resend verification code" };
      }
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.detail || error.message,
        };
      } else {
        return { success: false, error: error.message };
      }
    }
  };

  // Forgot password - send reset code to email
  const forgotPassword = async (email) => {
    try {
      const response = await ApiService.post("auth/forgot-password", {
        email: email,
      });

      if (response.status === 200 || response.status === 201) {
        return { success: true };
      } else {
        return { success: false, error: "Failed to send reset code" };
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 404) {
          return {
            success: false,
            error: "Виникла помилка. Спробуйте пізніше.",
          };
        } else if (error.response.status === 400) {
          return {
            success: false,
            error: "Неправильна пошта. Спробуйте ще раз.",
          };
        }
        return {
          success: false,
          error: error.response.data?.detail || error.message,
        };
      } else {
        return { success: false, error: error.message };
      }
    }
  };

  // Reset password with code
  const resetPassword = async (email, code, newPassword, confirmPassword) => {
    try {
      const response = await ApiService.post("auth/reset-password", {
        email: email,
        code: code,
        new_password: newPassword,
      });

      if (response.status === 200 || response.status === 201) {
        return { success: true };
      } else {
        return { success: false, error: "Failed to reset password" };
      }
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          error: error.response.data?.detail || error.message,
        };
      } else {
        return { success: false, error: error.message };
      }
    }
  };

  // Delete user account
  const deleteAccount = async () => {
    try {
      await ApiService.delete("users");
      // Optionally, log out the user after account deletion
      logout();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error?.message,
      };
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    token,
    sessionExpired,
    clearSessionExpired,
    login,
    loginWithGoogle,
    register,
    logout,
    updateUser,
    getUserInfo,
    changePassword,
    deleteAccount,
    verifyEmail,
    resendVerificationCode,
    forgotPassword,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
